/**
 * Round 20 V6 -- a supplier's contracts, no-objection and exit plan.
 *
 * The supplier page showed only open findings and regulatory
 * obligations: for the cash-in-transit provider, material and behind an
 * admitted MVB service, nothing said it held no contract and no exit
 * plan, and an auditor read "Exit plans (external)" elsewhere as no plan
 * held for any supplier.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { Card } from "shared/components/Card";
import { listOf } from "shared/i18n/listOf";

interface Arrangement {
  contract_ref: string;
  title: string;
  material_outsourcing: boolean;
  end_date: string | null;
  sama_no_objection_ref: string | null;
  exit_strategy_summary: string | null;
  services_not_supplied?: string[];
}

interface VendorArrangements {
  material: boolean;
  exit_plan_ref: string | null;
  contracts: Arrangement[];
  exit_plan_missing_behind: string[];
}

export function VendorArrangementsPanel({
  vendorId,
}: {
  vendorId: string | undefined;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["vendor-arrangements", vendorId],
    queryFn: () =>
      api.get<VendorArrangements>(`/v1/entities/vendor/${vendorId}/arrangements`),
    enabled: Boolean(vendorId),
  });
  if (!q.data) return null;
  const d = q.data;
  return (
    <Card
      testid="vendor-arrangements"
      title={t("vendorArrangements.heading")}
      icon="ibs"
    >
      {(d.exit_plan_missing_behind ?? []).length > 0 && (
        <p
          data-testid="vendor-no-exit-plan"
          className="mb-2 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs text-rose-800"
        >
          {t("vendorArrangements.noExitPlan", {
            services: listOf(d.exit_plan_missing_behind),
          })}
        </p>
      )}
      <p className="text-xs text-slate-600">
        {d.exit_plan_ref
          ? t("vendorArrangements.exitPlanRef", { ref: d.exit_plan_ref })
          : t("vendorArrangements.noExitPlanRef")}
      </p>
      {d.contracts.length === 0 ? (
        <p data-testid="vendor-no-contracts" className="mt-1 text-xs text-slate-600">
          {t("vendorArrangements.noContracts")}
        </p>
      ) : (
        <ul className="mt-1 space-y-1 text-xs text-slate-700">
          {d.contracts.map((c) => (
            <li key={c.contract_ref} data-testid={`vendor-contract-${c.contract_ref}`}>
              <span className="font-mono">{c.contract_ref}</span> · {c.title}
              {c.material_outsourcing ? ` · ${t("vendorArrangements.material")}` : ""}
              {" · "}
              {c.sama_no_objection_ref
                ? t("vendorArrangements.noObjection", { ref: c.sama_no_objection_ref })
                : c.material_outsourcing
                  ? t("vendorArrangements.noObjectionMissing")
                  : t("vendorArrangements.noObjectionNotNeeded")}
              {" · "}
              {c.exit_strategy_summary
                ? t("vendorArrangements.exitRecorded")
                : t("vendorArrangements.exitMissing")}
              {c.end_date
                ? ` · ${t("vendorArrangements.ends", { date: c.end_date })}`
                : ""}
              {/* Round 20 V7 -- services named that the supplier does not
                  reach on the vendor register. */}
              {(c.services_not_supplied ?? []).length > 0 && (
                <span
                  data-testid={`vendor-contract-unsupplied-${c.contract_ref}`}
                  className="block text-rose-700"
                >
                  {t("supplierCoherence.contractRow", {
                    services: (c.services_not_supplied ?? []).join(", "),
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
